import { useState, useEffect, useCallback } from 'react'
import { apiFetch, ApiError } from '../lib/api'
import { documentReaderHref } from '../lib/documentReader'
import DocumentLink from './documents/DocumentLink'
import type { TaskQueueItem } from '../lib/tasks'
import { obligationLabel, failureLabel } from '../lib/claims'
import { cleanEngineText, formatOfficerDate, formatOfficerValue } from '../lib/officerFormat'
import { getCurrentUser } from '../lib/auth'
import { evaluateGates, primaryFlips, sourceMatches, type OvGate } from '../lib/ovGates'
import { SELF_VERIFICATION_RULE } from '../lib/roleVerbs'
import { HelpDot } from './guidance/Guidance'
import Sentence, { LinkedSentence } from './Sentence'
import { OptionalChip, OptionalList } from './OptionalEvidence'
import { demandWords, KIND_WORD, type ObjKind } from './fund/evidenceRows'
import { fetchEvidenceMatrix } from './fund/fundArea'
import EvidenceDecisionPane from './evidence/EvidenceDecisionPane'
import './canon.css'
import './optionalevidence.css'

/* The v13 OV card (locked 2026-08-31) — spec of record: mock-ov-atlas
 * (all 9 obligations) + the hybrid in mock-ov-reco-compare; Tier-1 C1
 * "role honesty + four-eyes" (2026-09-04, role-verbs.png) turned the
 * condition rows into a TABLE and put the first pair of eyes on the page.
 *
 * One card per document per obligation. No chart — the identity line
 * names the object (type-colored word). Green question ("Does X prove
 * …?"), then the gate TABLE in the ruled ORDER: about the right object
 * FIRST, then accepted source (chips, the matching one lit), fully
 * signed (counted), fresh enough (cutoff vs document date) — and
 * "Confirms the record" LAST, one row per compared field with the
 * document value beside the record value and a page door. A gate that
 * FAILS reads ✗ in rust with the compared pair ("document 9,500 · record
 * 8,000"); when any gate of a mandatory demand fails the primary verb is
 * "Reject — falls short" (reason open, recorded as a gate failure under
 * the officer's name) and "Approve anyway…" is secondary, asking for a
 * reason recorded as an override.
 *
 * Four-eyes: "booked by <name> · <date>" names the officer who booked the
 * object; that officer sees both verbs disabled with the rule ("you
 * booked this — a second officer must verify") — the same words the
 * server answers with (403) if the click is forced.
 *
 * Verbs: POST /api/tasks/{id}/resolve {outcome: approve|reject, note}. */

interface ObligationRow {
  id: string
  object_type: string
  object_id: string
  obligation_code: string
  status: string
  waived_reason: string | null
  obligation: {
    code: string
    family: string
    level: string
    is_mandatory: boolean
    accepted_sources: string[]
    stale_after_months?: number | null
    description: string
  } | null
  proof: Array<{
    field: string
    document_value: string | null
    record_value: string | null
    source_page?: number | null
    source_snippet?: string | null
  }>
  proof_document_filename: string | null
  signature_caveat: string | null
  document_date?: string | null
  document_type?: string | null
  claim_source_type?: string | null
  four_eyes_required?: boolean
  freshness_cutoff?: string | null
  signature_blocks?: { present?: boolean; unsigned?: number | null } | null
  failures: Array<{
    failure: string
    document_id: string | null
    detail: string | null
    recorded_at: string
  }>
  booked_by_user_id?: string | null
  booked_by_name?: string | null
  booked_at?: string | null
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: unknown } | undefined)?.detail
    if (typeof d === 'string') return d
    return `${e.statusText} (${e.status})`
  }
  return e instanceof Error ? e.message : 'request failed'
}

function dataStr(data: TaskQueueItem['task_data'], key: string): string | null {
  if (!data || typeof data !== 'object') return null
  const v = (data as Record<string, unknown>)[key]
  return typeof v === 'string' && v ? v : null
}

const OBJECT_WORD_CLASS: Record<string, string> = {
  entity: 't-ent',
  asset: 't-ent',
  instrument: 't-ins',
  position: 't-pos',
}

const GATE_TESTID: Record<OvGate['key'], string> = {
  object: 'ov-right-object',
  source: 'ov-accepted-source',
  signed: 'ov-signed',
  fresh: 'ov-freshness',
  confirms: 'ov-confirms',
}

type Mode = 'idle' | 'reject' | 'override'

export default function EvidenceApprovalPane(props: {
  task: TaskQueueItem
  onResolved: () => void
}) {
  const documentId = dataStr(props.task.task_data, 'document_id')
  if (props.task.target_type === 'evidence_status' && documentId && props.task.target_id) {
    return (
      <EvidenceDecisionPane
        {...props}
        requirementId={props.task.target_id}
        documentId={documentId}
      />
    )
  }
  return <LegacyEvidenceApprovalPane {...props} />
}

function LegacyEvidenceApprovalPane({
  task,
  onResolved,
}: {
  task: TaskQueueItem
  onResolved: () => void
}) {
  const isStatusRow = task.target_type === 'evidence_status'
  const documentId = dataStr(task.task_data, 'document_id')
  const me = getCurrentUser()

  const [obRow, setObRow] = useState<ObligationRow | null>(null)
  /* The fund's OPTIONAL demands still owed — what policy only recommends
   * (Ale 2026-09-05). The sign-off never waits on them, so they are OFF
   * by default and behind the same switch the fund's chain-status block
   * and Evidence tab read; the officer can open the list and request them
   * in the same breath instead of coming back later. Fail-soft: no matrix,
   * no chip. */
  const [optional, setOptional] = useState<
    { key: string; code: string; words: string; object: string }[]
  >([])
  useEffect(() => {
    if (!task.fund_id) return setOptional([])
    let cancelled = false
    fetchEvidenceMatrix(task.fund_id)
      .then((rows) => {
        if (cancelled) return
        setOptional(
          rows
            .filter((r) => !r.is_mandatory && r.status !== 'satisfied' && r.status !== 'waived')
            .map((r) => ({
              key: `${r.object_type}:${r.object_id}:${r.obligation_code}`,
              code: r.obligation_code,
              words: demandWords(r.obligation_code),
              object: KIND_WORD[r.object_type as ObjKind] ?? r.object_type,
            }))
        )
      })
      .catch(() => {
        if (!cancelled) setOptional([])
      })
    return () => {
      cancelled = true
    }
  }, [task.fund_id])
  const [targetTitle, setTargetTitle] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>('idle')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!isStatusRow || !task.target_id) return
    try {
      const row = await apiFetch<ObligationRow>(`/api/evidence-status/${task.target_id}`)
      setObRow(row)
      // A failing mandatory gate opens the reject reason at once — the
      // primary verb is already "Reject — falls short".
      setMode(primaryFlips(row, evaluateGates(row)) ? 'reject' : 'idle')
      const objectType = row.object_type === 'asset' ? 'entity' : row.object_type
      if (task.fund_id && ['entity', 'instrument', 'position', 'trade'].includes(objectType)) {
        apiFetch<{ title: string }>(
          `/api/funds/${task.fund_id}/objects/${objectType}/${row.object_id}`
        )
          .then((d) => setTargetTitle(d.title))
          .catch(() => {})
      }
    } catch {
      /* row unreadable — the task summary below still renders */
    }
  }, [isStatusRow, task.target_id, task.fund_id])

  useEffect(() => {
    setObRow(null)
    setTargetTitle(null)
    setMode('idle')
    setNote('')
    load()
  }, [load])

  async function decide(outcome: 'approve' | 'reject', override = false) {
    setBusy(true)
    setError(null)
    const reason = note.trim()
    try {
      await apiFetch(`/api/tasks/${task.task_id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({
          outcome,
          note: override && reason ? `override — ${reason}` : reason || undefined,
        }),
      })
      setDone(
        outcome === 'approve'
          ? override
            ? 'Evidence approved despite the failing gate — recorded as an override under your name.'
            : 'Evidence approved — the sign-off settles the obligation under your name.'
          : 'Evidence rejected — the obligation stays open, with your reason on file.'
      )
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  const readerContext = (page?: number | null) => {
    const kind = obRow?.object_type === 'asset' ? 'entity' : obRow?.object_type
    return {
      page,
      fundId: task.fund_id,
      focus:
        kind && obRow?.object_id
          ? `${kind === 'position' ? 'segment' : kind}:${obRow.object_id}`
          : null,
      returnTo: window.location.pathname + window.location.search,
    }
  }

  if (done)
    return (
      <div className="vcard" style={{ flex: 1, alignSelf: 'flex-start', padding: 22 }}>
        <div style={{ fontSize: 'var(--text-13)', color: 'var(--verified)', fontWeight: 600 }}>
          ✓ {done}
        </div>
        <button className="vbtn pri" style={{ marginTop: 16 }} onClick={onResolved}>
          Next task →
        </button>
      </div>
    )

  const code = obRow?.obligation_code ?? dataStr(task.task_data, 'obligation_code')
  const objectType = obRow?.object_type === 'asset' ? 'entity' : (obRow?.object_type ?? 'entity')
  const objectWordClass = OBJECT_WORD_CLASS[objectType] ?? ''
  const isTrade = objectType === 'trade'
  const gates = obRow ? evaluateGates(obRow) : []
  const flips = obRow ? primaryFlips(obRow, gates) : false
  const isBooker = !!(
    me?.user_id &&
    obRow?.booked_by_user_id &&
    obRow.booked_by_user_id === me.user_id
  )
  const needsSecondOfficer = isBooker && obRow?.four_eyes_required !== false
  const bookedAt = formatOfficerDate(obRow?.booked_at?.slice(0, 10) ?? null)
  const noteReady = !!note.trim()

  const door = (page: number | null) =>
    documentId ? (
      <DocumentLink
        className="cn-docchip"
        document={{ document_id: documentId }}
        context={readerContext(page)}
        aria-label={`Open the document${page ? ` at page ${page}` : ''}`}
      />
    ) : null

  /** The ✓ finding, composed from the row's own facts. */
  const okFinding = (g: OvGate) => {
    switch (g.key) {
      case 'object':
        return targetTitle ? `names ${targetTitle}` : 'names the object this demand is about'
      case 'source':
        return (
          <>
            {(obRow?.obligation?.accepted_sources ?? []).map((s) => (
              <span
                key={s}
                className={`cn-src${sourceMatches(s, obRow?.claim_source_type) ? ' hit' : ''}`}
              >
                {s.replace(/_/g, ' ')}
              </span>
            ))}
          </>
        )
      case 'signed':
        return obRow?.signature_blocks?.unsigned === 0
          ? 'every signature space signed'
          : 'signature spaces present'
      case 'fresh':
        return 'dated after the cutoff policy allows'
      case 'confirms':
        return g.field ? (
          <Sentence
            meaning="ov.confirms"
            values={{
              field: g.field.replace(/_/g, ' '),
              value: g.document != null ? formatOfficerValue(g.document) : null,
            }}
          />
        ) : (
          'The compared fields match the record.'
        )
    }
  }

  /** The document / record column. */
  const values = (g: OvGate) => {
    switch (g.key) {
      case 'object':
        return targetTitle ?? ''
      case 'source':
        return g.document ?? ''
      case 'signed':
        return g.ok ? '' : `${obRow?.signature_blocks?.unsigned ?? '?'} unsigned`
      case 'fresh':
        return (
          <>
            <span className="lbl">doc</span>
            {formatOfficerDate(g.document) ?? '—'}
            <span className="lbl">cutoff</span>
            {formatOfficerDate(g.record) ?? '—'}
          </>
        )
      case 'confirms':
        if (g.document == null) return ''
        return g.ok ? (
          formatOfficerValue(g.document)
        ) : (
          <>
            <span className="lbl">document</span>
            {formatOfficerValue(g.document)}
            <span className="lbl">record</span>
            {g.record != null ? formatOfficerValue(g.record) : '—'}
          </>
        )
    }
  }

  const gateLabel = (g: OvGate) => {
    switch (g.key) {
      case 'object':
        return `About the right ${isTrade ? 'trade' : objectType}`
      case 'source':
        return 'Accepted source'
      case 'signed':
        return 'Fully signed'
      case 'fresh':
        return 'Fresh enough'
      case 'confirms':
        return 'Confirms the record'
    }
  }

  let confirmsSeen = 0
  const testIdFor = (g: OvGate) => {
    if (g.key !== 'confirms') return GATE_TESTID[g.key]
    confirmsSeen += 1
    return confirmsSeen === 1 ? 'ov-confirms' : 'ov-confirms-more'
  }

  return (
    <div
      className="cnv"
      style={{ flex: 1, alignSelf: 'flex-start' }}
      data-testid="ov-card"
      data-gate-state={obRow ? (flips ? 'fail' : 'pass') : 'unknown'}
      data-four-eyes={needsSecondOfficer ? 'booker' : 'clear'}
    >
      <div className="cn-back" />
      <div className="cn-card">
        <div className="cn-ctit">
          <span className="k verify">Verify</span>
          <HelpDot k="pane.evidence_approval" />
          {documentId ? (
            <DocumentLink
              className="cn-opensrc"
              data-testid="evidence-doc-chip"
              document={{ document_id: documentId }}
              context={readerContext()}
            />
          ) : null}
        </div>

        <div className="cn-ident" data-testid="ov-identity">
          {isTrade ? 'trade' : <span className={objectWordClass}>{objectType}</span>}
          {targetTitle ? ` — ${targetTitle}` : code ? ` — ${obligationLabel(code)}` : ''}
        </div>

        <div className="cn-qblock verify" data-testid="ov-question">
          <LinkedSentence
            slot="document"
            documentRef={documentId ? { document_id: documentId } : null}
            href={documentId ? documentReaderHref(documentId, readerContext()) : null}
            className="qq"
            meaning="ov.question"
            values={{
              document: documentId ? 'Document' : null,
              obligation: code ? obligationLabel(code).toLowerCase() : null,
              object: targetTitle,
            }}
          />
          {obRow?.obligation ? (
            <div className="qw">
              {obRow.obligation.is_mandatory ? 'Mandatory demand. ' : 'Recommended demand. '}
              Everything policy requires for this document to count:
            </div>
          ) : null}
        </div>

        {obRow ? (
          <>
            <div className="cn-gt" role="table" aria-label="What policy requires of this document">
              <div className="cn-gt-h" role="row">
                <span aria-hidden="true" />
                <span role="columnheader">gate</span>
                <span role="columnheader">finding</span>
                <span role="columnheader" className="vals">
                  document · record
                </span>
                <span role="columnheader" className="door">
                  page
                </span>
              </div>
              {gates.map((g) => (
                <div
                  key={`${g.key}:${g.field ?? ''}`}
                  className={`cn-gt-r${g.ok ? '' : ' miss'}`}
                  role="row"
                  data-testid={testIdFor(g)}
                  data-gate={g.key}
                  data-gate-failed={g.ok ? undefined : 'true'}
                >
                  <span className="st" role="cell">
                    {g.ok ? '✓' : '✗'}
                  </span>
                  <span className="gate" role="cell">
                    {gateLabel(g)}
                  </span>
                  <span className="find" role="cell">
                    {g.ok ? okFinding(g) : g.failing}
                  </span>
                  <span className="vals" role="cell">
                    {values(g)}
                  </span>
                  <span className="door" role="cell">
                    {door(g.page)}
                  </span>
                </div>
              ))}
            </div>

            {obRow.signature_caveat ? (
              <div className="cn-dm" data-testid="signature-caveat">
                <span className="st miss">⚠</span>
                <span className="txt">
                  <small>{obRow.signature_caveat}</small>
                </span>
              </div>
            ) : null}

            {obRow.booked_by_name ? (
              <div className="cn-booked" data-testid="ov-booked-by">
                <span>
                  booked by <b>{obRow.booked_by_name}</b>
                  {bookedAt ? ` · ${bookedAt}` : ''}
                </span>
                {obRow.four_eyes_required !== false ? (
                  <span className="cn-tag4">four-eyes</span>
                ) : null}
              </div>
            ) : null}

            {obRow.failures.length > 0 ? (
              <div
                className="cn-note"
                style={{ padding: '4px 16px 0' }}
                data-testid="gate-failures"
              >
                Earlier documents fell short:{' '}
                {obRow.failures
                  .map(
                    (f) =>
                      `${failureLabel(f.failure)}${
                        f.detail ? ` — ${cleanEngineText(f.detail)}` : ''
                      }`
                  )
                  .join(' · ')}
              </div>
            ) : null}
          </>
        ) : task.description ? (
          <div className="cn-dm">
            <span className="st">✓</span>
            <span className="txt">
              <small>{cleanEngineText(task.description)}</small>
            </span>
          </div>
        ) : null}

        {error ? <div className="cn-errline">{error}</div> : null}

        {needsSecondOfficer ? (
          <>
            <div className="cn-abar">
              <button
                type="button"
                className="cn-btn p verify blocked"
                disabled
                data-testid="evidence-reject"
              >
                Reject — falls short
              </button>
              <button
                type="button"
                className="cn-btn blocked"
                disabled
                data-testid="evidence-approve"
              >
                {flips ? 'Approve anyway…' : 'Approve — the document proves it'}
              </button>
            </div>
            <div className="cn-abar-note">
              <span className="cn-rule" data-testid="ov-four-eyes-refusal">
                {SELF_VERIFICATION_RULE}
              </span>
            </div>
          </>
        ) : (
          <>
            {mode !== 'idle' ? (
              <div style={{ padding: '4px 16px' }}>
                <div className="cn-reason-l">
                  {mode === 'reject' ? (
                    <>
                      Why does this document fall short?{' '}
                      <small>recorded as a gate failure under your name</small>
                    </>
                  ) : (
                    <>
                      Why approve despite the failing gate?{' '}
                      <small>recorded as an override under your name</small>
                    </>
                  )}
                </div>
                <textarea
                  aria-label={
                    mode === 'reject'
                      ? 'Why this document falls short'
                      : 'Why approve despite the failing gate'
                  }
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={
                    mode === 'reject'
                      ? 'Why does this document fall short? Recorded as a gate failure under your name.'
                      : 'Why does the document count despite the failing gate? Recorded as an override under your name.'
                  }
                  rows={3}
                  data-testid={
                    mode === 'reject' ? 'evidence-reject-note' : 'evidence-override-note'
                  }
                  style={{
                    width: '100%',
                    fontSize: 'var(--text-12)',
                    border: '1px solid var(--cn-line)',
                    borderRadius: 8,
                    padding: 8,
                    resize: 'vertical',
                    font: 'inherit',
                  }}
                />
              </div>
            ) : null}

            {/* Recommended, still missing — never a gate on this sign-off. */}
            <div className="cn-optbar" style={{ marginTop: 10 }}>
              <OptionalChip n={optional.length} testId="ov-optional-toggle" />
            </div>
            <OptionalList
              demands={optional}
              fundId={task.fund_id ?? null}
              testId="ov-optional-list"
            />

            <div className="cn-abar">
              {flips ? (
                mode === 'override' ? (
                  <>
                    <button
                      type="button"
                      className="cn-btn p verify"
                      data-testid="evidence-approve"
                      disabled={busy || !noteReady}
                      onClick={() => decide('approve', true)}
                    >
                      Approve with this reason
                    </button>
                    <button
                      type="button"
                      className="cn-btn"
                      disabled={busy}
                      onClick={() => {
                        setNote('')
                        setMode('reject')
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="cn-btn p verify"
                      data-testid="evidence-reject-confirm"
                      disabled={busy || !noteReady}
                      onClick={() => decide('reject')}
                    >
                      Reject — falls short
                    </button>
                    <button
                      type="button"
                      className="cn-btn"
                      data-testid="evidence-approve-anyway"
                      disabled={busy}
                      onClick={() => {
                        setNote('')
                        setMode('override')
                      }}
                    >
                      Approve anyway…
                    </button>
                  </>
                )
              ) : mode === 'reject' ? (
                <>
                  <button
                    type="button"
                    className="cn-btn p verify"
                    data-testid="evidence-reject-confirm"
                    disabled={busy || !noteReady}
                    onClick={() => decide('reject')}
                  >
                    Reject with this reason
                  </button>
                  <button
                    type="button"
                    className="cn-btn"
                    disabled={busy}
                    onClick={() => {
                      setNote('')
                      setMode('idle')
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="cn-btn p verify"
                    data-testid="evidence-approve"
                    disabled={busy}
                    onClick={() => decide('approve')}
                  >
                    Approve — the document proves it
                  </button>
                  <button
                    type="button"
                    className="cn-btn"
                    data-testid="evidence-reject"
                    disabled={busy}
                    onClick={() => setMode('reject')}
                  >
                    Reject — falls short
                  </button>
                </>
              )}
            </div>
            {flips && mode === 'reject' ? (
              <div className="cn-abar-note" data-testid="ov-override-note">
                Approve anyway asks for a reason and is recorded as an override under your name.
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
